import { normalizeSeriesTypeId } from "../types/pane-descriptor";
const registry = new Map();
const seriesStyleOverrides = new Map();
const seriesStyleListeners = new Map();
const seriesStyleChangeListeners = new Set();
/** Đọc `params.period` nếu hợp lệ, ngược lại dùng fallback của chỉ báo. */
export function periodFromConfig(config, fallback) {
    const period = config.params?.period;
    return typeof period === "number" && Number.isFinite(period) ? period : fallback;
}
function colorFromConfig(config, fallback) {
    return typeof config.color === "string" && config.color.length > 0 ? config.color : fallback;
}
function normalizeSeriesStyleOverride(partialStyle) {
    const normalized = {};
    if (typeof partialStyle.color === "string" && partialStyle.color.length > 0) {
        normalized.color = partialStyle.color;
    }
    if (typeof partialStyle.lineWidth === "number" && Number.isFinite(partialStyle.lineWidth)) {
        normalized.lineWidth = partialStyle.lineWidth;
    }
    if (typeof partialStyle.visible === "boolean") {
        normalized.visible = partialStyle.visible;
    }
    if (typeof partialStyle.opacity === "number" && Number.isFinite(partialStyle.opacity)) {
        normalized.opacity = Math.min(1, Math.max(0, partialStyle.opacity));
    }
    if (Array.isArray(partialStyle.dashPattern)) {
        const dashPattern = partialStyle.dashPattern.filter((value) => typeof value === "number" && Number.isFinite(value) && value > 0);
        if (dashPattern.length > 0) {
            normalized.dashPattern = dashPattern;
        }
    }
    return normalized;
}
function cloneSeriesStyleOverride(style) {
    if (!style) {
        return undefined;
    }
    return {
        ...style,
        dashPattern: style.dashPattern ? [...style.dashPattern] : undefined,
    };
}
function notifySeriesStyleChange(instanceId) {
    seriesStyleListeners.get(instanceId)?.forEach((callback) => callback());
    seriesStyleChangeListeners.forEach((callback) => callback());
}
function notifyAllSeriesStyleChanges() {
    for (const [instanceId, listeners] of seriesStyleListeners) {
        listeners.forEach((callback) => callback());
    }
    seriesStyleChangeListeners.forEach((callback) => callback());
}
function dashPatternToStrokeDasharray(dashPattern) {
    if (!dashPattern || dashPattern.length === 0) {
        return "Solid";
    }
    if (dashPattern.length === 1) {
        return dashPattern[0] <= 2 ? "Dot" : "Dash";
    }
    return dashPattern[0] <= 2 ? "Dot" : "Dash";
}
export function registerLineSeries(type, options) {
    registerSeries(type, {
        component: options.component,
        defaultParams: options.defaultParams ?? {},
        defaultYAxis: options.defaultYAxis ?? "right",
        tooltipEntry: options.tooltipEntry,
        settingsFields: options.settingsFields,
    });
}
export function clearRegistry() {
    registry.clear();
}
export function clearSeriesStyleOverrides() {
    seriesStyleOverrides.clear();
    notifyAllSeriesStyleChanges();
}
export function listSeriesStyleOverrides() {
    return Object.fromEntries([...seriesStyleOverrides.entries()].map(([instanceId, style]) => [instanceId, cloneSeriesStyleOverride(style) ?? {}]));
}
export function overrideSeriesStyle(instanceId, partialStyle) {
    const normalized = normalizeSeriesStyleOverride(partialStyle);
    const current = seriesStyleOverrides.get(instanceId) ?? {};
    const next = { ...current, ...normalized };
    seriesStyleOverrides.set(instanceId, next);
    notifySeriesStyleChange(instanceId);
}
export function getSeriesStyleOverride(instanceId) {
    return cloneSeriesStyleOverride(seriesStyleOverrides.get(instanceId));
}
export function clearSeriesStyleOverride(instanceId) {
    seriesStyleOverrides.delete(instanceId);
    notifySeriesStyleChange(instanceId);
}
export function subscribeSeriesStyle(instanceId, callback) {
    if (!seriesStyleListeners.has(instanceId)) {
        seriesStyleListeners.set(instanceId, new Set());
    }
    seriesStyleListeners.get(instanceId).add(callback);
    return () => {
        const listeners = seriesStyleListeners.get(instanceId);
        if (!listeners) {
            return;
        }
        listeners.delete(callback);
        if (listeners.size === 0) {
            seriesStyleListeners.delete(instanceId);
        }
    };
}
export function subscribeSeriesStyleChanges(callback) {
    seriesStyleChangeListeners.add(callback);
    return () => {
        seriesStyleChangeListeners.delete(callback);
    };
}
export function registerSeries(type, entry) {
    registry.set(type, entry);
}
export function getSeries(type) {
    const entry = registry.get(type);
    if (!entry) {
        throw new Error(`SeriesRegistry: unknown type "${type}"`);
    }
    return entry;
}
export function listRegistered() {
    return [...registry.keys()];
}
const SERIES_TIER_ORDER = ["free", "pro", "enterprise"];
/** S3b: tier của series (vắng entry.tier = "free"). Ném lỗi như getSeries nếu type lạ. */
export function getSeriesTier(type) {
    return getSeries(type).tier ?? "free";
}
/** S3b: tier hiện tại có được thêm series này không (pure, fail-closed về free). */
export function isSeriesAvailableForTier(type, currentTier) {
    return SERIES_TIER_ORDER.indexOf(currentTier) >= SERIES_TIER_ORDER.indexOf(getSeriesTier(type));
}
/**
 * S5b: map tier thô (PAT 7 mức / plan server / null) về SeriesTier 3 mức.
 * starter/none/khuyết → free; custom/internal → enterprise (full).
 */
export function toSeriesTier(tier) {
    if (tier === "pro")
        return "pro";
    if (tier === "enterprise" || tier === "custom" || tier === "internal")
        return "enterprise";
    return "free";
}
/**
 * S3b-1: lọc series theo tier (dùng cho render-gate tập trung ở DynamicChart).
 * Chuẩn hóa alias trước khi tra tier; type lạ → loại (fail-closed, không render).
 */
export function filterAvailableSeries(series, currentTier) {
    return series.filter((s) => {
        try {
            return isSeriesAvailableForTier(normalizeSeriesTypeId(s.type), currentTier);
        }
        catch {
            return false;
        }
    });
}
